'use client';

import { CatDropdown } from '@blocks-react/components/Dropdown';
import { CatList } from '@blocks-react/components/List';
import { CatListItem } from '@blocks-react/components/ListItem';
import { useDropdownController } from '../../../../hooks/form-controllers/useDropdownController';
import { Conditional } from '../../../common';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { ReadonlyValue } from '../read-only-value';
import { BlocksSelectEvent } from '@blocks-web-components/events';

interface ListItem {
  label: string;
  value: string;
  default?: boolean;
}

interface Props {
  name: string;
  label: string;
  placeholder: string;
  itemList: Array<ListItem>;
  item?: ListItem;
  required?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  loading?: boolean;
  showDropdown?: boolean;
  selectedValue?: string;
  status?: 'error' | 'warning';
  errorNote?: string;
  onSelect?: (e: BlocksSelectEvent) => void;
  position?: 'auto' | 'bottom-start';
}
export const AssociatedDealerDropdown = ({
  name,
  label,
  placeholder,
  itemList,
  item,
  required = false,
  disabled = false,
  readOnly = false,
  loading = false,
  showDropdown = true,
  selectedValue,
  status,
  errorNote,
  onSelect,
  position = 'auto'
}: Props) => {
  const { setValue, resetField } = useFormContext();
  const { dropdownField } = useDropdownController({ name }, onSelect);
  const firstItem = itemList?.[0];
  const defaultItem = itemList?.find((item) => item?.default);
  const isSingleItem = itemList?.length === 1;
  const showItemList = itemList?.length > 1;
  const selectedItem = itemList?.find((item) => item.value === selectedValue);

  /**
   * Sets the value of the field on first render and whenever itemList changes,
   * if an item is provided, we set that item as the value,
   * if there's only 1 item on the itemList, we set that item as the value,
   * if there's multiple values, we set the item that has default === true
   */
  useEffect(() => {
    if (item) {
      setValue(name, item.label);
    } else if (isSingleItem && firstItem) {
      setValue(name, firstItem.label);
    } else if (selectedItem) {
      setValue(name, selectedItem.label);
    } else if (defaultItem) {
      setValue(name, defaultItem.label);
    } else {
      resetField(name);
    }
    return () => {
      resetField(name);
    };
  }, [item, itemList]);
  
  return (
    <>
      <Conditional test={!readOnly && showDropdown}>
        <CatDropdown
          {...dropdownField}
          fieldId={name}
          name={name}
          label={label}
          title={label}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          isReadonly={readOnly}
          isLoading={loading}
          data-testid={`${name}-test`}
          className={'data-cs-mask'}
          key={`adp-${name}-${dropdownField.value}`}
          position={position}
          isInline={false}
          status={status}
          errorNote= {errorNote}
        > 
          <Conditional test={showItemList}>
            <CatList>
              {itemList?.map((item) => (
                <CatListItem
                  key={`${name}-option-${item?.value}`}
                  value={item?.label}
                >
                  {item?.label}
                </CatListItem>
              ))}
            </CatList>
          </Conditional>
        </CatDropdown>
      </Conditional>
      <Conditional test={readOnly}>
        <ReadonlyValue
          label={label}
          required={required}
          value={dropdownField.value}
        />
      </Conditional>
    </>
  );
};
